import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface DeviceIdState {
  deviceId: string | null
  getOrCreateDeviceId: () => string
}

/**
 * A random, anonymous per-install id — not tied to any login (there isn't
 * one yet). Exists solely so the Feedback feature can correlate a
 * submission with a later "thank you" note (see feedbackClient.ts). Never
 * sent anywhere except Supabase for that one purpose, never used for
 * tracking/ads/analytics.
 *
 * Generated lazily (only when getOrCreateDeviceId() is first called, e.g.
 * from opening the Feedback modal), not eagerly on module load, so a user
 * who never touches that feature never gets a new localStorage key written.
 * Separate store/key from progressStore — identity isn't gamification
 * progress and shouldn't be reset by anything that touches that store.
 */
export const useDeviceIdStore = create<DeviceIdState>()(
  persist(
    (set, get) => ({
      deviceId: null,
      getOrCreateDeviceId: () => {
        const existing = get().deviceId
        if (existing) return existing
        const id = crypto.randomUUID()
        set({ deviceId: id })
        return id
      },
    }),
    {
      name: 'kaleido-device-id',
    },
  ),
)

/** Non-hook accessor for use outside components (e.g. feedbackClient.ts). */
export function getDeviceId(): string {
  return useDeviceIdStore.getState().getOrCreateDeviceId()
}

/** True once a device id has actually been created — lets callers (e.g. the
 * startup thank-you check) skip a network call entirely for a brand-new
 * user who's never opened the Feedback modal. */
export function hasDeviceId(): boolean {
  return useDeviceIdStore.getState().deviceId !== null
}
